import { useCallback, useMemo } from 'react'
import { OverrideAuthorizationFn, PermissionConfig } from './types'

export function usePermissionExpressionValidator(
  allPermissions: string[],
  permissions?: PermissionConfig,
  overrideAuthorization?: OverrideAuthorizationFn
) {
  const and = useCallback((...permissions: string[]): string => {
    if (!permissions.length) return ''
    if (permissions.length === 1) return permissions[0]

    return `(${permissions.join('&')})`
  }, [])

  const or = useCallback((...permissions: string[]): string => {
    if (!permissions.length) return ''
    if (permissions.length === 1) return permissions[0]

    return `(${permissions.join('|')})`
  }, [])

  const not = useCallback((permission: string): string => {
    if (!permission.length) return ''
    return `!${permission}`
  }, [])

  const parsePermissionExpression = useCallback(
    (expr: string, all: string[]) => {
      if (!expr.startsWith('(')) {
        if (expr === 'true' || expr === 'false') return expr === 'true'

        return expr.startsWith('!')
          ? !all.includes(expr.substring(1))
          : all.includes(expr)
      }
      if (!expr.endsWith(')')) throw new Error('Invalid permission expression')

      const evaluate = (e: string) => {
        const sep = e.includes('&') ? '&' : e.includes('|') ? '|' : null
        if (!sep)
          return e.startsWith('!')
            ? String(!all.includes(e.substring(1)))
            : String(all.includes(e))

        const invert = (perm: string) =>
          perm.startsWith('!')
            ? !all.includes(perm.substring(1))
            : all.includes(perm)

        const perms = e.split(sep)

        if (sep === '&') {
          let result = true
          for (const perm of perms) {
            if (perm === 'true') continue
            if (perm === 'false') return String(false)
            result &&= invert(perm)
          }

          return String(result)
        } else {
          let result = false
          for (const perm of perms) {
            if (perm === 'true') return String(true)
            if (perm === 'false') continue
            if (invert(perm)) return String(true)
          }

          return String(result)
        }
      }

      const pattern = /\([^()]+\)/
      const recurse = (e: string): string => {
        let matches: RegExpExecArray | null
        while ((matches = pattern.exec(e)) !== null) {
          const match = matches[0].slice(1, -1)
          const result = evaluate(match)
          e =
            e.substring(0, matches.index) +
            result +
            e.substring(matches.index + match.length + 2)
        }
        return e
      }

      const result = recurse(expr)
      return result === 'true'
    },
    []
  )

  const isAuthorized = useMemo(() => {
    if (typeof overrideAuthorization !== 'undefined') {
      const override = overrideAuthorization(permissions, allPermissions)
      if (override !== null) {
        return override
      }
    }

    if (!permissions) return true

    if (typeof permissions === 'string')
      return allPermissions.includes(permissions)
    if (Array.isArray(permissions))
      return permissions.every(perm => allPermissions.includes(perm))

    return parsePermissionExpression(
      permissions({
        and,
        or,
        not,
      }),
      allPermissions
    )
  }, [
    overrideAuthorization,
    permissions,
    allPermissions,
    parsePermissionExpression,
    and,
    or,
    not,
  ])

  return isAuthorized
}
